/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/anchor-has-content */
import {process, State} from "@progress/kendo-data-query"
import {saveAs} from "@progress/kendo-file-saver"
import {Button} from "@progress/kendo-react-buttons"
import {ExcelExport, ExcelExportColumn, KendoOoxml} from "@progress/kendo-react-excel-export"
import {
    Grid,
    GridCellProps,
    GridColumn,
    GridColumnMenuCheckboxFilter,
    GridColumnMenuFilter,
    GridColumnMenuProps,
    GridDataStateChangeEvent,
    GridToolbar,
} from "@progress/kendo-react-grid"
import {useInternationalization} from "@progress/kendo-react-intl"
import React, {createContext, useEffect, useState} from "react"
import {Vehicle} from "../../../containers/Vehicles/vehicleSlice"
import {RootState} from "../../../rootReducer"
import {columnProps} from "../../../shared/ActiveGridFilter"
import {SpinnerGrid} from "../../../shared/CommonItems"
import {useAppSelector} from "../../../store"
import {processExportData} from "../../../utils/common"
import {PermissionsChecker} from "../../Shared/permissionChecker"
import AnnualTestGrid from "../AnnualTestGrid"

export const AnnualTestsContext = createContext({
    vehicles: [] as Vehicle[],
    toggleAnnualTestExpansion: (vehicleId: number | null, annualTestId: number | null, isExpanded: boolean) => {},
})

export const vehicleProvider = AnnualTestsContext.Provider

const VehicleGrid = ({showAddDialog}: {showAddDialog: any}) => {
    let _exporter: any
    const initialState: State = {skip: 0, take: 100}

    const intl = useInternationalization()

    const gridRef = React.useRef<any>()

    const vehicledata = useAppSelector((state: RootState) => state.containers.vehicles.vehicleData)
    const isVehicleLoading = useAppSelector((state: RootState) => state.containers.vehicles.isVehicleLoading)

    const [vehicles, setVehicles] = useState<Vehicle[]>(vehicledata ?? [])
    const [dataState, setDataState] = useState(initialState)

    const [notFixedColumnWidth, setNotFixedColumnWidth] = useState<number | undefined>(
        window.innerWidth > 1600 ? undefined : 220,
    )

    const handleResize = () => {
        setNotFixedColumnWidth(window.innerWidth > 1600 ? undefined : 220)
    }

    useEffect(() => {
        if (notFixedColumnWidth === undefined) {
            gridRef.current.headerRef.current.table.style.removeProperty("width")
            gridRef.current.tableElement.style.removeProperty("width")
        }
    }, [notFixedColumnWidth])

    useEffect(() => {
        window.addEventListener("resize", handleResize)
        return () => {
            window.removeEventListener("resize", handleResize)
        }
    }, [])

    const CommonColumnMenu = (props: GridColumnMenuProps) => (
        <GridColumnMenuFilter {...props} expanded={true} hideSecondFilter={true} />
    )

    const formatExcelDateCell = (value: any) => {
        const dateVal = value == null || value === "" ? "" : intl.formatDate(new Date(value))
        return dateVal
    }

    useEffect(() => {
        setVehicles(vehicledata)
        setDataState(initialState)
    }, [vehicledata])

    const toggleVehicleExpansion = (vehicleId: number, isExpanded: boolean) =>
        vehicles && setVehicles(vehicles.map(x => (x.vehicleId === vehicleId ? {...x, expanded: isExpanded} : x)))

    const toggleAnnualTestExpansion = (vehicleId: number | null, annualTestId: number | null, isExpanded: boolean) =>
        vehicles &&
        setVehicles(
            vehicles.map(x => {
                if (x.vehicleId === vehicleId) {
                    x.annualTests = x.annualTests.map((i: any) =>
                        i.annualTestId === annualTestId ? {...i, expanded: isExpanded} : i,
                    )
                }
                return x
            }),
        )

    const exportExcel = (vehicles: Vehicle[]) => {
        let workbook = _exporter.workbookOptions()
        let rows = workbook.sheets[0].rows
        let headerOptions = rows[0].cells[0]

        let count = 0

        for (let vehicleIndex = vehicles!.length - 1; vehicleIndex >= 0; vehicleIndex--) {
            if (vehicles != null) {
                for (let testIndex = vehicles[vehicleIndex].annualTests.length - 1; testIndex >= 0; testIndex--) {
                    const test = vehicles[vehicleIndex].annualTests[testIndex]
                    rows.splice(vehicleIndex + 2, 0, {
                        cells: [
                            null,
                            formatExcelDateCell(test.testDate),
                            test.testType,
                            test.testResult,
                            test.testCertificateNumber,
                            formatExcelDateCell(test.expiryDate),
                            test.numberOfDefectsAtTest,
                            test.numberOfAdvisoryDefectsAtTest,
                            test.location,
                        ].map(cellValue => (cellValue === null ? {} : {value: cellValue})),
                    })

                    for (let defectIndex = test.defects.length - 1; defectIndex >= 0; defectIndex--) {
                        const defect = test.defects[defectIndex]
                        if (defectIndex === test.defects.length - 1) {
                            rows.splice(vehicleIndex + 3, 0, {
                                cells: [
                                    null,
                                    null,
                                    "Item Number",
                                    "Reason",
                                    "Severity Code",
                                    "Severity Description",
                                ].map(cellValue =>
                                    cellValue === null ? {} : Object.assign({}, headerOptions, {value: cellValue}),
                                ),
                            })
                        }
                        rows.splice(vehicleIndex + 4, 0, {
                            cells: [
                                null,
                                null,
                                defect.failureItemNo,
                                defect.failureReason,
                                defect.severityCode,
                                defect.severityDescription,
                            ].map(cellValue => (cellValue === null ? {} : {value: cellValue})),
                        })
                    }
                }
                if (vehicles[vehicleIndex].annualTests.length > 0) {
                    rows.splice(vehicleIndex + 2, 0, {
                        cells: [
                            null,
                            "Test Date",
                            "Test Type",
                            "Test result",
                            "Certificate number",
                            "Expiry Date",
                            "Defects",
                            "Advisory defects",
                            "Location",
                        ].map(cellValue =>
                            cellValue === null ? {} : Object.assign({}, headerOptions, {value: cellValue}),
                        ),
                    })
                }

                count++
                if (count >= vehicles!.length) {
                    //resolve
                }
            }
        }
        new KendoOoxml.Workbook(workbook).toDataURL().then((dataUrl: any) => {
            saveAs(dataUrl, crypto.randomUUID())
        })
    }

    const formatExcelData = (vehicledata: Vehicle[]) =>
        vehicledata!.map(({manufactureDate, registrationDate, annualTestExpiryDate, ...item}) => ({
            manufactureDate: formatExcelDateCell(manufactureDate),
            registrationDate: formatExcelDateCell(registrationDate),
            annualTestExpiryDate: formatExcelDateCell(annualTestExpiryDate),
            ...item,
        }))

    const ExpandSelectionCell = (props: GridCellProps) => {
        const vehicle: Vehicle = props.dataItem
        return (
            <td className="k-hierarchy-cell">
                {props.dataItem.annualTests.length > 0 && (
                    <a
                        onClick={() => toggleVehicleExpansion(vehicle.vehicleId, !vehicle.expanded)}
                        className={`k-icon k-i-${vehicle.expanded ? "minus" : "plus"}`}
                        href="#"
                    ></a>
                )}
            </td>
        )
    }
    const ColumnMenuCheckboxFilter = (props: any, data: Vehicle[] | null) => (
        <GridColumnMenuCheckboxFilter {...props} data={data} expanded={true} />
    )

    return (
        <div className="position-relative">
            {isVehicleLoading && <SpinnerGrid />}
            {vehicles && (
                <>
                    <ExcelExport
                        data={formatExcelData(processExportData(vehicles, dataState))}
                        ref={exporter => (_exporter = exporter)}
                    >
                        <ExcelExportColumn field="expanded" hidden={true}></ExcelExportColumn>
                        <ExcelExportColumn field="registration" title="Vehicle"></ExcelExportColumn>
                        <ExcelExportColumn field="make" title="Make"></ExcelExportColumn>
                        <ExcelExportColumn field="model" title="Model"></ExcelExportColumn>
                        <ExcelExportColumn field="vehicleType" title="Vehicle Type"></ExcelExportColumn>
                        <ExcelExportColumn field="vehicleClass" title="Vehicle Class"></ExcelExportColumn>
                        <ExcelExportColumn field="manufactureDate" title="Manufacture Date" />
                        <ExcelExportColumn field="registrationDate" title="Registration Date"></ExcelExportColumn>
                        <ExcelExportColumn field="annualTestExpiryDate" title="Annual Test Expiry Date" />
                    </ExcelExport>
                    <AnnualTestsContext.Provider value={{vehicles, toggleAnnualTestExpansion}}>
                        <Grid
                            style={{
                                height: "calc(100vh - 14.3rem)",
                            }}
                            data={process(vehicles, dataState)}
                            {...dataState}
                            onDataStateChange={(e: GridDataStateChangeEvent) => setDataState(e.dataState)}
                            sortable={true}
                            pageable={true}
                            detail={AnnualTestGrid}
                            expandField="expanded"
                            ref={gridRef}
                        >
                            <GridToolbar>
                                <PermissionsChecker permission={["Vehicles.Add"]}>
                                    <Button
                                        fillMode="flat"
                                        themeColor={"primary"}
                                        className="buttons-container-button"
                                        iconClass="fa fa-plus"
                                        onClick={showAddDialog}
                                    >
                                        New Vehicle
                                    </Button>
                                </PermissionsChecker>
                                <Button
                                    fillMode={"flat"}
                                    themeColor={"primary"}
                                    className="buttons-container-button"
                                    iconClass="fa fa-file-excel"
                                    onClick={() => exportExcel(processExportData(vehicles, dataState))}
                                    disabled={process(vehicles, dataState).total === 0}
                                >
                                    Export to Excel
                                </Button>
                            </GridToolbar>

                            <GridColumn
                                field="expanded"
                                cell={ExpandSelectionCell}
                                width="40px"
                                headerCell={() => <></>}
                                columnMenu={CommonColumnMenu}
                            />
                            <GridColumn
                                {...columnProps("registration", dataState)}
                                field="registration"
                                title="Vehicle"
                                width={notFixedColumnWidth}
                            />
                            <GridColumn {...columnProps("make", dataState)} field="make" title="Make" width="187px" />
                            <GridColumn
                                {...columnProps("model", dataState)}
                                field="model"
                                title="Model"
                                width={notFixedColumnWidth}
                            />
                            <GridColumn
                                {...columnProps("vehicleType", dataState)}
                                field="vehicleType"
                                title="Vehicle Type"
                                columnMenu={props => ColumnMenuCheckboxFilter(props, vehicledata)}
                                width={notFixedColumnWidth}
                            />
                            <GridColumn
                                {...columnProps("vehicleClass", dataState)}
                                field="vehicleClass"
                                title="Vehicle Class"
                                columnMenu={props => ColumnMenuCheckboxFilter(props, vehicledata)}
                                width={notFixedColumnWidth}
                            />
                            <GridColumn
                                {...columnProps("manufactureDate", dataState)}
                                field="manufactureDate"
                                title="Manufacture Date"
                                filter={"date"}
                                format="{0:d}"
                                width={notFixedColumnWidth}
                            />
                            <GridColumn
                                {...columnProps("registrationDate", dataState)}
                                field="registrationDate"
                                title="Registration Date"
                                filter={"date"}
                                format="{0:d}"
                                width={notFixedColumnWidth}
                            />
                            <GridColumn
                                {...columnProps("annualTestExpiryDate", dataState)}
                                field="annualTestExpiryDate"
                                title="Annual Test Expiry Date"
                                filter={"date"}
                                format="{0:d}"
                                width={notFixedColumnWidth}
                            />
                        </Grid>
                    </AnnualTestsContext.Provider>
                </>
            )}
        </div>
    )
}

export default VehicleGrid
